// @storybook-skip: internal section of LessonsExploreScreen; covered by its screen story
import { useTranslation } from "react-i18next";
import { ScrollView } from "react-native";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { ConnectedButtonGroup } from "../ConnectedButtonGroup";
import { LessonsViewModeToggle } from "./LessonsViewModeToggle";
import type { LessonStatusFilter, LessonViewMode } from "./types";

type Props = {
	title: string;
	statusFilter: LessonStatusFilter;
	onChangeStatusFilter: (filter: LessonStatusFilter) => void;
	viewMode: LessonViewMode;
	onChangeViewMode: (viewMode: LessonViewMode) => void;
	showViewToggle: boolean;
	isMobile: boolean;
};

export const LessonsExploreFilterBar = ({
	title,
	statusFilter,
	onChangeStatusFilter,
	viewMode,
	onChangeViewMode,
	showViewToggle,
	isMobile,
}: Props) => {
	const { t } = useTranslation();
	const theme = useAppTheme();

	const statusButtons = (
		[
			{ value: "all", labelKey: "lessons.explore.statusFilter.all" },
			{
				value: "inProgress",
				labelKey: "lessons.explore.statusFilter.inProgress",
			},
			{
				value: "notStarted",
				labelKey: "lessons.explore.statusFilter.notStarted",
			},
			{
				value: "completed",
				labelKey: "lessons.explore.statusFilter.completed",
			},
		] as const
	).map(({ value, labelKey }) => ({
		value,
		label: t(labelKey),
		labelStyle: isMobile
			? { ...theme.fonts.labelMedium, letterSpacing: 0 }
			: undefined,
		style: isMobile
			? ({ flexBasis: "auto", flexShrink: 0, minWidth: 0 } as const)
			: undefined,
	}));

	const statusFilterGroup = (
		<ConnectedButtonGroup
			value={statusFilter}
			onValueChange={(value) =>
				onChangeStatusFilter(value as LessonStatusFilter)
			}
			buttons={statusButtons}
			style={isMobile ? { flex: 1 } : undefined}
		/>
	);

	if (isMobile) {
		return (
			<ScrollView
				horizontal
				showsHorizontalScrollIndicator={false}
				contentContainerStyle={{ flexGrow: 1 }}
			>
				{statusFilterGroup}
			</ScrollView>
		);
	}

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			justifyContent="space-between"
			gap={16}
		>
			<Text variant="titleLarge">{title}</Text>
			<Box flexDirection="row" alignItems="center" gap={16}>
				<Box width={440}>{statusFilterGroup}</Box>
				{showViewToggle ? (
					<LessonsViewModeToggle
						viewMode={viewMode}
						onChangeViewMode={onChangeViewMode}
					/>
				) : null}
			</Box>
		</Box>
	);
};
